import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ClipboardList, RefreshCw, Mail, ExternalLink,
  CheckCircle2, Clock, AlertCircle, XCircle, Gift, CreditCard,
  Search, ChevronRight, CheckCircle, AlertCircle as AlertCircleIcon
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  attivo: {
    label: "Attivo",
    icon: CheckCircle2,
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  trial: {
    label: "Trial",
    icon: Clock,
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    dot: "bg-blue-500",
  },
  trial_carta: {
    label: "Trial (carta salvata)",
    icon: CreditCard,
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    text: "text-indigo-700",
    dot: "bg-indigo-500",
  },
  trial_scaduto: {
    label: "Trial scaduto",
    icon: AlertCircle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    dot: "bg-amber-500",
  },
  fallito: {
    label: "Pagamento fallito",
    icon: XCircle,
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    dot: "bg-red-500",
  },
  scaduto: {
    label: "Scaduto",
    icon: XCircle,
    bg: "bg-gray-50",
    border: "border-gray-200",
    text: "text-gray-500",
    dot: "bg-gray-400",
  },
  gratuito: {
    label: "Gratuito",
    icon: Gift,
    bg: "bg-purple-50",
    border: "border-purple-200",
    text: "text-purple-700",
    dot: "bg-purple-400",
  },
  pendente: {
    label: "Pendente",
    icon: Clock,
    bg: "bg-slate-50",
    border: "border-slate-200",
    text: "text-slate-600",
    dot: "bg-slate-400",
  },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pendente;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 10);
  }
};

const SUPPORT_EMAIL = "support@yollo.me";

const RegistrazioniLogPanel = () => {
  const [activeTab, setActiveTab] = useState("registrazioni");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("tutti");

  // Email log state
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSearch, setEmailSearch] = useState("");
  const [emailTotal, setEmailTotal] = useState(0);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("sa_token");
      const res = await axios.get(`${API}/superadmin/registrazioni-log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const fetchEmailLogs = useCallback(async (q = "") => {
    setEmailLoading(true);
    try {
      const token = localStorage.getItem("sa_token");
      const res = await axios.get(`${API}/superadmin/email-logs?limit=100&q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailLogs(res.data.logs || []);
      setEmailTotal(res.data.total || 0);
    } catch { /* ignore */ }
    setEmailLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === "email") fetchEmailLogs();
  }, [activeTab, fetchEmailLogs]);

  const filtered = items.filter((item) => {
    const matchSearch = !search ||
      item.nome?.toLowerCase().includes(search.toLowerCase()) ||
      item.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "tutti" || item.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // Count by status
  const counts = items.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, {});

  const FILTER_TABS = [
    { key: "tutti", label: "Tutti", count: items.length },
    { key: "trial", label: "Trial", count: (counts.trial || 0) + (counts.trial_carta || 0) + (counts.trial_scaduto || 0) },
    { key: "attivo", label: "Attivi", count: counts.attivo || 0 },
    { key: "fallito", label: "Falliti", count: counts.fallito || 0 },
    { key: "scaduto", label: "Scaduti", count: counts.scaduto || 0 },
  ];

  return (
    <div className="space-y-5" data-testid="registrazioni-log-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <ClipboardList className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-black tracking-tight text-slate-900">
              {activeTab === "registrazioni" ? "Log Registrazioni" : "Log Email Inviate"}
            </h2>
            <p className="text-xs text-slate-400">
              {activeTab === "registrazioni" ? `${items.length} account totali` : `${emailTotal} email totali`}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm"
          onClick={() => activeTab === "registrazioni" ? fetchLog() : fetchEmailLogs(emailSearch)}
          data-testid="refresh-log-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
        </Button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab("registrazioni")}
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === "registrazioni" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-registrazioni"
        >
          <ClipboardList className="h-3.5 w-3.5 inline mr-1.5" />Registrazioni
        </button>
        <button
          onClick={() => setActiveTab("email")}
          className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${activeTab === "email" ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="tab-email-log"
        >
          <Mail className="h-3.5 w-3.5 inline mr-1.5" />Email Log
        </button>
      </div>

      {/* ═══════════ TAB: REGISTRAZIONI ═══════════ */}
      {activeTab === "registrazioni" && (<>

      {/* Status summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { key: "attivo", label: "Attivi", color: "text-emerald-600", bg: "bg-emerald-50" },
          { key: "trial", label: "In Trial", color: "text-blue-600", bg: "bg-blue-50", count: (counts.trial || 0) + (counts.trial_carta || 0) },
          { key: "trial_scaduto", label: "Trial Scaduti", color: "text-amber-600", bg: "bg-amber-50" },
          { key: "fallito", label: "Falliti", color: "text-red-600", bg: "bg-red-50" },
        ].map(({ key, label, color, bg, count }) => (
          <div key={key} className={`${bg} rounded-xl p-3 border border-opacity-50`} data-testid={`stat-${key}`}>
            <p className={`text-2xl font-black ${color}`}>{count !== undefined ? count : (counts[key] || 0)}</p>
            <p className="text-xs text-slate-500 font-medium">{label}</p>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome o email..."
            className="pl-10 rounded-xl bg-white border-slate-200"
            data-testid="search-registrazioni"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto">
          {FILTER_TABS.map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilterStatus(key === filterStatus ? "tutti" : key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                filterStatus === key
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
              data-testid={`filter-${key}`}
            >
              {label} {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-5 h-5 animate-spin text-indigo-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center border border-slate-200">
          <ClipboardList className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Nessuna registrazione trovata</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => {
            const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pendente;
            return (
              <div
                key={item.societa_id}
                className={`bg-white rounded-xl border p-4 transition-all hover:shadow-sm ${cfg.border}`}
                data-testid={`reg-${item.societa_id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Left: status dot + name */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 text-sm truncate">{item.nome}</p>
                      {item.email && (
                        <a
                          href={`mailto:${item.email}`}
                          className="text-xs text-slate-400 hover:text-indigo-600 flex items-center gap-1 mt-0.5"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`email-${item.societa_id}`}
                        >
                          <Mail className="h-3 w-3" />
                          {item.email}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Center: status + date */}
                  <div className="flex flex-wrap items-center gap-3 sm:gap-5 flex-shrink-0">
                    <StatusBadge status={item.status} />

                    {item.giorni_rimasti !== null && item.giorni_rimasti !== undefined && (
                      <span className="text-xs text-slate-500">
                        {item.giorni_rimasti > 0
                          ? <><strong className="text-blue-600">{item.giorni_rimasti}</strong> gg rimasti</>
                          : <span className="text-amber-600 font-semibold">Scaduto</span>
                        }
                      </span>
                    )}

                    <div className="text-right">
                      <p className="text-[10px] text-slate-400">Registrato il</p>
                      <p className="text-xs font-semibold text-slate-600">{formatDate(item.data_registrazione)}</p>
                    </div>
                  </div>

                  {/* Right: support link */}
                  <div className="flex-shrink-0">
                    <a
                      href={`mailto:${SUPPORT_EMAIL}?subject=Supporto%20cliente%20${encodeURIComponent(item.nome)}&body=Cliente%3A%20${encodeURIComponent(item.nome)}%0AEmail%3A%20${encodeURIComponent(item.email)}%0AStatus%3A%20${item.status}`}
                      className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-semibold px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition-colors"
                      data-testid={`support-link-${item.societa_id}`}
                    >
                      <ExternalLink className="h-3 w-3" />
                      Supporto
                    </a>
                  </div>
                </div>

                {/* Extra details for failed/trial_scaduto */}
                {(item.status === "fallito" || item.status === "trial_scaduto") && (
                  <div className="mt-2 pt-2 border-t border-dashed border-slate-100">
                    <p className="text-xs text-slate-500">
                      {item.status === "fallito"
                        ? "Carta non addebitata. Contattare il cliente per aggiornare il metodo di pagamento."
                        : `Trial di ${item.trial_giorni} giorni scaduto. In attesa di addebito dallo scheduler.`}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>)}

      {/* ═══════════ TAB: EMAIL LOG ═══════════ */}
      {activeTab === "email" && (
        <div className="space-y-3">
          {/* Barra ricerca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Cerca per destinatario o oggetto..."
              className="pl-9 rounded-xl h-9 text-sm"
              value={emailSearch}
              onChange={(e) => { setEmailSearch(e.target.value); fetchEmailLogs(e.target.value); }}
              data-testid="email-log-search"
            />
          </div>

          {emailLoading ? (
            <div className="text-center py-10 text-slate-400 text-sm">Caricamento...</div>
          ) : emailLogs.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">
              <Mail className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Nessuna email registrata
            </div>
          ) : (
            <div className="space-y-2">
              {emailLogs.map((log) => (
                <div
                  key={log.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border ${log.success ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}
                  data-testid={`email-log-row-${log.id}`}
                >
                  <div className={`mt-0.5 flex-shrink-0 ${log.success ? "text-emerald-600" : "text-red-500"}`}>
                    {log.success ? <CheckCircle className="h-4 w-4" /> : <AlertCircleIcon className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-slate-800 truncate">{log.to}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${log.success ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {log.success ? "Inviata" : "Errore"}
                      </span>
                      <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-md">{log.provider}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-0.5 truncate">{log.subject}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(log.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegistrazioniLogPanel;
